import { Controller } from "@hotwired/stimulus"

const ANIMATION_SAFETY_MS = 300
const RESIZE_DEBOUNCE_MS = 100

export default class extends Controller {
  static targets = ["short", "full", "toggle", "image"]
  static values = { direction: { type: String, default: "right" } }

  get imageHiddenClass() {
    return this.directionValue === "left" ? "read-more-image-hidden-left" : "read-more-image-hidden"
  }

  connect() {
    this.expanded = false
    this.animating = false
    this.shortTarget.classList.add("read-more-text-animatable")
    this.fullTarget.classList.add("read-more-text-animatable")
    this.syncTextState()
    this.resetImageState()
    this._resizeTimer = null
    this._onResize = () => {
      clearTimeout(this._resizeTimer)
      this._resizeTimer = setTimeout(() => this.handleResize(), RESIZE_DEBOUNCE_MS)
    }
    window.addEventListener("resize", this._onResize)
  }

  disconnect() {
    window.removeEventListener("resize", this._onResize)
    clearTimeout(this._resizeTimer)
  }

  handleResize() {
    if (!this.hasImageTarget) return

    if (this.expanded) {
      this.imageTarget.classList.add("read-more-image-gone")
    } else {
      this.resetImageState()
    }
  }

  toggle(event) {
    event.preventDefault()
    if (this.animating) return

    if (!this.imageVisible()) {
      this.toggleWithoutImage()
      return
    }

    if (this.expanded) {
      this.animating = true
      this.hideTargetAnimated(this.fullTarget, () => {
        this.expanded = false
        this.setToggleText("read more")
        this.shortTarget.classList.remove("hidden")
        this.shortTarget.classList.add("read-more-text-off")
        void this.shortTarget.getBoundingClientRect()
        this.slideImageIn(() => {
          this.showTargetAnimated(this.shortTarget, () => {
            this.animating = false
          })
        })
      })
      return
    }

    this.slideImageOutThenExpand()
  }

  syncTextState() {
    if (this.expanded) {
      this.shortTarget.classList.add("hidden")
      this.fullTarget.classList.remove("hidden")
      this.setToggleText("read less")
      return
    }

    this.shortTarget.classList.remove("hidden")
    this.fullTarget.classList.add("hidden")
    this.setToggleText("read more")
  }

  setToggleText(text) {
    this.toggleTargets.forEach(t => t.textContent = text)
  }

  imageVisible() {
    if (!this.hasImageTarget) return false
    const img = this.imageTarget
    const cls = this.imageHiddenClass
    const hadGone = img.classList.contains("read-more-image-gone")
    const hadHidden = img.classList.contains(cls)
    if (hadGone) img.classList.remove("read-more-image-gone")
    if (hadHidden) img.classList.remove(cls)
    const visible = img.offsetParent !== null
    if (hadGone) img.classList.add("read-more-image-gone")
    if (hadHidden) img.classList.add(cls)
    return visible
  }

  resetImageState() {
    if (!this.hasImageTarget) return
    this.imageTarget.classList.remove(this.imageHiddenClass, "read-more-image-gone")
  }

  slideImageOutThenExpand() {
    this.animating = true
    let finished = false
    const finish = () => {
      if (finished) return
      finished = true
      this.imageTarget.removeEventListener("transitionend", onImageHidden)
      this.imageTarget.classList.add("read-more-image-gone")
      this.expanded = true
      this.setToggleText("read less")
      this.shortTarget.classList.add("hidden")
      this.showTargetAnimated(this.fullTarget, () => {
        this.animating = false
      })
    }
    const onImageHidden = (event) => {
      if (event.propertyName !== "transform") return
      finish()
    }
    this.imageTarget.addEventListener("transitionend", onImageHidden)
    this.imageTarget.classList.add(this.imageHiddenClass)
    setTimeout(finish, ANIMATION_SAFETY_MS)
  }

  slideImageIn(done) {
    if (!this.hasImageTarget) { done(); return }
    const cls = this.imageHiddenClass
    this.imageTarget.classList.remove("read-more-image-gone")
    this.imageTarget.classList.add(cls)
    void this.imageTarget.getBoundingClientRect()
    let finished = false
    const finish = () => {
      if (finished) return
      finished = true
      this.imageTarget.removeEventListener("transitionend", onImageShown)
      done()
    }
    const onImageShown = (event) => {
      if (event.propertyName !== "transform") return
      finish()
    }
    this.imageTarget.addEventListener("transitionend", onImageShown)
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.imageTarget.classList.remove(cls)
      })
    })
    setTimeout(finish, ANIMATION_SAFETY_MS)
  }

  toggleWithoutImage() {
    this.animating = true
    if (this.expanded) {
      this.hideTargetAnimated(this.fullTarget, () => {
        this.expanded = false
        this.setToggleText("read more")
        this.resetImageState()
        this.showTargetAnimated(this.shortTarget, () => {
          this.animating = false
        })
      })
      return
    }

    this.expanded = true
    this.setToggleText("read less")
    this.shortTarget.classList.add("hidden")
    this.showTargetAnimated(this.fullTarget, () => {
      this.animating = false
    })
  }

  showTargetAnimated(target, done) {
    target.classList.remove("hidden", "read-more-text-off")
    target.classList.add("read-more-text-off")
    void target.getBoundingClientRect()
    let finished = false
    const finish = () => {
      if (finished) return
      finished = true
      done()
    }
    target.addEventListener("transitionend", (event) => {
      if (event.propertyName !== "transform") return
      finish()
    }, { once: true })
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        target.classList.remove("read-more-text-off")
      })
    })
    setTimeout(finish, ANIMATION_SAFETY_MS)
  }

  hideTargetAnimated(target, done) {
    target.classList.remove("read-more-text-off")
    void target.getBoundingClientRect()
    let finished = false
    const finish = () => {
      if (finished) return
      finished = true
      target.classList.add("hidden")
      target.classList.remove("read-more-text-off")
      done()
    }
    target.addEventListener("transitionend", (event) => {
      if (event.propertyName !== "transform") return
      finish()
    }, { once: true })
    target.classList.add("read-more-text-off")
    setTimeout(finish, ANIMATION_SAFETY_MS)
  }
}
